import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"

import {
    SessionInfo,
    getSessionInfoColorSchema,
    renderSessionInfoAttendanceModalOpener as renderAttendanceModalOpener,
    renderSessionInfoHeaderAvatar as renderHeaderAvatar,
    renderSessionInfoHeaderChevron as renderHeaderChevron,
    renderSessionInfoHeaderDropdown as renderHeaderDropdown,
    renderSessionInfoHeaderNameTooltip as renderHeaderNameTooltip,
    renderSessionInfoNotice as renderNotice,
    renderSessionInfoAttendanceListItem,
    useSessionInfoAttendeeListPrefetcher as useAttendeeListPrefetcher,
    useSessionInfoRenderPropsFunctions as useRenderPropsFunctions
} from "@/3514/components"
import { ECoachingSessionsGroupQueryKey } from "@/3514/config"
import { type TUserState, sessionTimeStateUtils, useUser } from "@/3514/hooks"
import { AttendanceModal } from "@/3514/modals"
import { ESessionModality } from "@/3514/types"
import { mapCoachingSessionsParticipantsToSessionInfoParticipants as participantsMapper } from "@/3514/utils"
import { emptyAsyncCallback } from "@/shared/types/functions"

import { type TCoachGroupSessionUiModel } from "./coach.group.ui.types"

type TProps = { session: TCoachGroupSessionUiModel }
const CoachGroupAssociatedSession: FC<TProps> = ({ session }: TProps): ReactElement => {
    const { user }: TUserState = useUser()

    const { renderButtonsProps, renderActionsProps, renderMessagesProps }: ReturnType<typeof useRenderPropsFunctions> =
        useRenderPropsFunctions(user.role)

    const onAttendeesListHover: ReturnType<typeof useAttendeeListPrefetcher> = useAttendeeListPrefetcher()

    return isNull(session) ? null : (
        <Fragment>
            <SessionInfo
                tag="section"
                viewerRole={user.role}
                isExpandable
                isDefaultExpanded
                withLiveChecker
                onLiveSessionStarted={emptyAsyncCallback}
                onLiveSessionPassed={emptyAsyncCallback}
                getColorSchema={getSessionInfoColorSchema}
                sessionsQueryKey={ECoachingSessionsGroupQueryKey({ groupId: session.groupId })}
                onAttendeesListHover={onAttendeesListHover}
                data={{
                    session: {
                        id: session.sessionId,
                        participants: participantsMapper(session.participants),
                        minHoursToEdit: session.allowedMinDaysToEdit,
                        groupId: session.groupId,
                        joinUrl: session.joinUrl,
                        duration: session.sessionDuration,
                        startDate: session.sessionTime,
                        name: session.sessionName,
                        modality: session.modality,
                        rescheduleCount: session.rescheduleHistory.length,
                        isMissed: session.isMissed,
                        isGifted: session.isGifted,
                        isSwappable: false,
                        isReschedulable: session.isRescheduleAllowed,
                        isAttendanceMarked: session.isAttendanceMarked,
                        isCancelled: session.isCancelled,
                        isExtraParty: false
                    },
                    user: { id: user.id, photo: user.photo, firstName: user.photo, lastName: user.lastName },
                    coach: isEmpty(session.coach)
                        ? { id: session.coachId, photo: null, firstName: null }
                        : { id: session.coachId, photo: session.coach.photo, firstName: session.coach.firstName },
                    extraParties: undefined
                }}
                renderers={{
                    renderNotice: renderNotice(user.role),
                    renderButtonsProps,
                    renderMessagesProps,
                    renderAttendanceListItem: renderSessionInfoAttendanceListItem,
                    renderHeaderDropdown: renderHeaderDropdown(renderActionsProps),
                    renderHeaderAvatar: renderHeaderAvatar(ESessionModality.Group, {
                        isGifted: false,
                        isGuest: false,
                        size: 45
                    }),
                    renderHeaderChevron: renderHeaderChevron({
                        isSessionInfoExpandable: true
                    }),
                    renderHeaderNameToolip: renderHeaderNameTooltip({
                        sessionName: session.sessionName,
                        sessionId: session.sessionId
                    }),
                    renderPostSessionModalOpener: undefined,
                    renderCancelledSessionModalOpener: undefined,
                    renderAttendanceModalOpener: renderAttendanceModalOpener({
                        isEnabled:
                            !session.isAttendanceMarked &&
                            !session.isMissed &&
                            !session.isCancelled &&
                            sessionTimeStateUtils.getIsSessionPast({
                                startDate: session.sessionTime,
                                durationInMins: session.sessionDuration
                            }),
                        session: {
                            modality: session.modality,
                            id: session.sessionId,
                            startDate: session.sessionTime,
                            minHoursToEdit: session.allowedMinDaysToEdit * 24
                        },
                        userId: user.id,
                        sessionsQueryKey: ECoachingSessionsGroupQueryKey({ groupId: session.groupId }),
                        groupId: session.groupId
                    })
                }}
            />

            <AttendanceModal />
        </Fragment>
    )
}

CoachGroupAssociatedSession.displayName = "CoachGroupAssociatedSession"

export default CoachGroupAssociatedSession
